/*
 * NicheCanvas cart frame swap.
 * Replaces one exact Gallery Wrap line with its framed variant while preserving
 * every line-item property. Shared by the UpCart drawer and the /cart page.
 */
(function () {
  'use strict';

  function copyProperties(properties) {
    var copy = {};
    Object.keys(properties || {}).forEach(function (key) { copy[key] = properties[key]; });
    return copy;
  }

  function sameProperties(left, right) {
    var a = left || {};
    var b = right || {};
    var aKeys = Object.keys(a);
    var bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (var i = 0; i < aKeys.length; i++) {
      var key = aKeys[i];
      if (!Object.prototype.hasOwnProperty.call(b, key) || a[key] !== b[key]) return false;
    }
    return true;
  }

  function requestJson(url, options, label) {
    return fetch(url, options || {}).then(function (response) {
      return response.json().catch(function () { return null; }).then(function (body) {
        if (!response.ok) {
          var error = new Error(label + ' failed (' + response.status + ')');
          error.status = response.status;
          error.body = body;
          throw error;
        }
        return body;
      });
    });
  }

  function readCart() {
    return requestJson('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }, 'cart read');
  }

  function changeQuantity(lineKey, quantity) {
    return requestJson('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }, 'cart change');
  }

  function findByKey(cart, lineKey) {
    var items = cart && cart.items ? cart.items : [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === lineKey) return items[i];
    }
    return null;
  }

  function getSellingPlanId(item) {
    var allocation = item && item.selling_plan_allocation;
    var plan = allocation && allocation.selling_plan;
    return plan && plan.id != null ? plan.id : null;
  }

  function isMatchingLine(item, identity) {
    var variantId = item.variant_id == null ? item.id : item.variant_id;
    var itemPlan = getSellingPlanId(item);
    var identityPlan = identity.sellingPlanId == null ? null : identity.sellingPlanId;
    return String(variantId) === String(identity.variantId)
      && sameProperties(item.properties, identity.properties)
      && (itemPlan == null && identityPlan == null || String(itemPlan) === String(identityPlan));
  }

  function matchingLines(cart, identity) {
    var items = cart && cart.items ? cart.items : [];
    var matches = [];
    for (var i = 0; i < items.length; i++) {
      if (isMatchingLine(items[i], identity)) matches.push(items[i]);
    }
    return matches;
  }

  function matchingQuantity(cart, identity) {
    var lines = matchingLines(cart, identity);
    var total = 0;
    for (var i = 0; i < lines.length; i++) total += lines[i].quantity;
    return total;
  }

  function findMatchingLine(cart, identity) {
    return matchingLines(cart, identity)[0] || null;
  }

  function resolveSource(cart, lineKey, fromVariantId, sourceProperties, sourceGenerationId,
      sourceSellingPlanId) {
    var exact = findByKey(cart, lineKey);
    if (exact) {
      var exactVariantId = exact.variant_id == null ? exact.id : exact.variant_id;
      var variantMatches = fromVariantId == null || String(exactVariantId) === String(fromVariantId);
      var propertiesMatch = sourceProperties == null || sameProperties(exact.properties, sourceProperties);
      var generationMatches = sourceGenerationId == null
        || String((exact.properties || {})._generation_id) === String(sourceGenerationId);
      var planMatches = sourceSellingPlanId === undefined
        || String(getSellingPlanId(exact)) === String(sourceSellingPlanId);
      if (variantMatches && propertiesMatch && generationMatches && planMatches) return exact;
    }

    // A key can become stale when automatic discounts recalculate. A captured property
    // snapshot (UpCart) or generation id (/cart) safely disambiguates personalized lines.
    var items = cart && cart.items ? cart.items : [];
    var matches = [];
    for (var i = 0; i < items.length; i++) {
      var variantId = items[i].variant_id == null ? items[i].id : items[i].variant_id;
      if (String(variantId) === String(fromVariantId)) matches.push(items[i]);
    }
    if (sourceSellingPlanId !== undefined) {
      matches = matches.filter(function (item) {
        return String(getSellingPlanId(item)) === String(sourceSellingPlanId);
      });
    }
    if (sourceProperties != null) {
      matches = matches.filter(function (item) { return sameProperties(item.properties, sourceProperties); });
      return matches[0] || null;
    }
    if (sourceGenerationId != null) {
      matches = matches.filter(function (item) {
        return String((item.properties || {})._generation_id) === String(sourceGenerationId);
      });
      if (!matches.length) return null;
      var firstProperties = matches[0].properties;
      var firstPlan = getSellingPlanId(matches[0]);
      for (var j = 1; j < matches.length; j++) {
        if (!sameProperties(matches[j].properties, firstProperties)
            || String(getSellingPlanId(matches[j])) !== String(firstPlan)) return null;
      }
      return matches[0];
    }
    return matches.length === 1 ? matches[0] : null;
  }

  function addIdentityQuantity(identity, quantity, label) {
    var item = { id: identity.variantId, quantity: quantity };
    if (Object.keys(identity.properties || {}).length) item.properties = copyProperties(identity.properties);
    if (identity.sellingPlanId != null) item.selling_plan = identity.sellingPlanId;
    return requestJson('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [item] })
    }, label);
  }

  function noRetryError(message) {
    var error = new Error(message);
    error.noRetry = true;
    return error;
  }

  function attemptSetMatchingQuantity(identity, desiredQuantity, expectedQuantity,
      requiredIdentity, requiredQuantity) {
    return readCart().then(function (cart) {
      if (requiredIdentity && matchingQuantity(cart, requiredIdentity) !== requiredQuantity) {
        throw noRetryError('framed line quantity changed during cart swap');
      }

      var lines = matchingLines(cart, identity);
      var total = matchingQuantity(cart, identity);
      if (total === desiredQuantity) return { done: true, cart: cart, currentQuantity: total };
      if (total !== expectedQuantity) throw noRetryError('source line quantity changed during cart swap');
      if (total < desiredQuantity) throw noRetryError('source line quantity fell below swap target');

      var quantityToRemove = total - desiredQuantity;
      var current = lines[0];
      for (var i = 1; i < lines.length; i++) {
        if (lines[i].quantity >= quantityToRemove) { current = lines[i]; break; }
        if (lines[i].quantity > current.quantity) current = lines[i];
      }
      var nextQuantity = Math.max(0, current.quantity - quantityToRemove);
      return changeQuantity(current.key, nextQuantity).then(function (updatedCart) {
        var updatedQuantity = matchingQuantity(updatedCart, identity);
        if (updatedQuantity < desiredQuantity) {
          throw noRetryError('source line quantity fell below swap target');
        }
        return {
          done: updatedQuantity === desiredQuantity,
          cart: updatedCart,
          currentQuantity: updatedQuantity
        };
      });
    });
  }

  function setMatchingQuantity(identity, desiredQuantity, expectedQuantity,
      requiredIdentity, requiredQuantity, attemptsLeft) {
    function run(currentExpected, remaining) {
      return attemptSetMatchingQuantity(
        identity,
        desiredQuantity,
        currentExpected,
        requiredIdentity,
        requiredQuantity
      ).then(function (result) {
        if (result.done) return result.cart;
        if (remaining > 1) return run(result.currentQuantity, remaining - 1);
        throw new Error('cart quantity did not reach the frame swap target');
      }, function (error) {
        if (!error.noRetry && remaining > 1) return run(currentExpected, remaining - 1);
        throw error;
      });
    }
    return run(expectedQuantity, attemptsLeft);
  }

  function restoreIdentityQuantity(identity, desiredQuantity) {
    return readCart().then(function (cart) {
      var currentQuantity = matchingQuantity(cart, identity);
      if (currentQuantity === desiredQuantity) return cart;
      if (currentQuantity < desiredQuantity) {
        return addIdentityQuantity(identity, desiredQuantity - currentQuantity, 'cart recovery add');
      }
      return setMatchingQuantity(identity, desiredQuantity, currentQuantity, null, 0, 10);
    }).then(function () {
      return readCart();
    }).then(function (cart) {
      if (matchingQuantity(cart, identity) !== desiredQuantity) {
        throw new Error('cart recovery quantity verification failed');
      }
      return cart;
    });
  }

  var sourceSnapshots = {};
  var activeSwap = false;

  function snapshot(options) {
    options = options || {};
    var requestedKey = String(options.lineKey || '');
    return readCart().then(function (cart) {
      var source = resolveSource(
        cart, requestedKey, options.fromVariantId, null, options.sourceGenerationId, undefined);
      if (!source) throw new Error('source cart line was not found for snapshot');
      var sourceVariantId = source.variant_id == null ? source.id : source.variant_id;
      var captured = {
        variantId: sourceVariantId,
        properties: copyProperties(source.properties),
        quantity: source.quantity,
        sellingPlanId: getSellingPlanId(source)
      };
      sourceSnapshots[requestedKey] = captured;
      return captured;
    });
  }

  function performSwap(options) {
    options = options || {};
    var requestedKey = String(options.lineKey || '');
    var fromVariantId = options.fromVariantId;
    var toVariantId = options.toVariantId;
    var captured = options.sourceSnapshot || sourceSnapshots[requestedKey] || null;
    var capturedProperties = captured ? captured.properties : null;
    var sourceGenerationId = options.sourceGenerationId;
    var sourceIdentity = null;
    var targetIdentity = null;
    var capturedSellingPlanId = captured ? captured.sellingPlanId : undefined;
    var sourceQuantityBefore = 0;
    var sourceQuantityAfter = 0;
    var targetQuantityBefore = 0;
    var targetQuantityAfter = 0;
    var selectedQuantity = 0;
    var addAttempted = false;

    if (!requestedKey || toVariantId == null) {
      return Promise.reject(new Error('frame swap requires a source line key and target variant'));
    }

    return readCart().then(function (cart) {
      var source = resolveSource(
        cart,
        requestedKey,
        fromVariantId,
        capturedProperties,
        sourceGenerationId,
        capturedSellingPlanId
      );
      if (!source) throw new Error('source cart line was not found uniquely');

      var properties = copyProperties(source.properties);
      var sourceVariantId = source.variant_id == null ? source.id : source.variant_id;
      var sellingPlanId = getSellingPlanId(source);
      sourceIdentity = {
        variantId: sourceVariantId,
        properties: properties,
        sellingPlanId: sellingPlanId
      };
      targetIdentity = {
        variantId: toVariantId,
        properties: properties,
        sellingPlanId: sellingPlanId
      };

      var fallbackQuantity = options.quantity != null ? options.quantity
        : (captured && captured.quantity != null ? captured.quantity : null);
      selectedQuantity = source.key === requestedKey ? source.quantity : parseInt(fallbackQuantity, 10);
      if (!(selectedQuantity > 0)) selectedQuantity = source.quantity;

      sourceQuantityBefore = matchingQuantity(cart, sourceIdentity);
      if (selectedQuantity > sourceQuantityBefore) throw new Error('source cart quantity is lower than the requested swap');
      sourceQuantityAfter = sourceQuantityBefore - selectedQuantity;
      targetQuantityBefore = matchingQuantity(cart, targetIdentity);
      targetQuantityAfter = targetQuantityBefore + selectedQuantity;

      addAttempted = true;
      return addIdentityQuantity(targetIdentity, selectedQuantity, 'frame add');
    }).then(function () {
      // Adding can recalculate discounts and split lines. Reconcile aggregate quantities
      // by stable variant + selling plan + exact properties, never by the pre-add key.
      return setMatchingQuantity(
        sourceIdentity,
        sourceQuantityAfter,
        sourceQuantityBefore,
        targetIdentity,
        targetQuantityAfter,
        10
      ).then(function (cart) {
        var sourceIsCorrect = matchingQuantity(cart, sourceIdentity) === sourceQuantityAfter;
        var targetIsCorrect = matchingQuantity(cart, targetIdentity) === targetQuantityAfter;
        if (sourceIsCorrect && targetIsCorrect) return cart;
        throw noRetryError('cart totals changed before frame swap verification');
      });
    }).catch(function (error) {
      if (!addAttempted || !sourceIdentity || !targetIdentity) throw error;

      // A failed response can still have mutated Shopify. Accept a verified success;
      // otherwise restore both identity groups to their exact pre-swap totals.
      return readCart().then(function (cart) {
        var sourceIsCorrect = matchingQuantity(cart, sourceIdentity) === sourceQuantityAfter;
        var targetIsCorrect = matchingQuantity(cart, targetIdentity) === targetQuantityAfter;
        if (sourceIsCorrect && targetIsCorrect) return cart;
        return restoreIdentityQuantity(sourceIdentity, sourceQuantityBefore)
          .then(function () { return restoreIdentityQuantity(targetIdentity, targetQuantityBefore); })
          .then(function () { throw error; });
      }).catch(function (recoveryError) {
        if (recoveryError === error) throw error;
        error.recoveryError = recoveryError;
        throw error;
      });
    });
  }

  function swap(options) {
    if (activeSwap) return Promise.reject(new Error('another frame swap is already in progress'));
    activeSwap = true;
    return performSwap(options).then(function (cart) {
      activeSwap = false;
      return cart;
    }, function (error) {
      activeSwap = false;
      throw error;
    });
  }

  function findAncestor(node, matcher) {
    while (node && node !== document) {
      if (matcher(node)) return node;
      node = node.parentNode;
    }
    return null;
  }

  function onCartPageUpgrade(event) {
    // While a swap is in flight, block every click in that cart row. This also
    // covers the Remove link, which cannot be disabled like a form control.
    var busyCartItem = findAncestor(event.target, function (node) {
      return node.tagName
        && node.tagName.toLowerCase() === 'nc-cart-item'
        && node.getAttribute('data-nc-frame-busy');
    });
    if (busyCartItem) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    var button = findAncestor(event.target, function (node) {
      return node.classList && node.classList.contains('btn-upsell');
    });
    if (!button) return;

    var cartItem = findAncestor(button, function (node) {
      return node.tagName && node.tagName.toLowerCase() === 'nc-cart-item';
    });
    if (!cartItem) return;

    var data;
    try { data = JSON.parse(button.getAttribute('data-upsell') || '{}'); } catch (e) { return; }
    if (!data.lineKey) return;

    event.preventDefault();
    event.stopPropagation();
    if (cartItem.getAttribute('data-nc-frame-busy')) return;

    cartItem.setAttribute('data-nc-frame-busy', '1');
    var quantityInput = cartItem.querySelector('input[name="quantity"]');
    var currentQuantity = quantityInput ? (parseInt(quantityInput.value, 10) || data.quantity) : data.quantity;
    var disabledControls = [];
    Array.prototype.forEach.call(cartItem.querySelectorAll('button,input,select'), function (item) {
      if (item.hasAttribute('disabled')) return;
      item.setAttribute('disabled', 'disabled');
      disabledControls.push(item);
    });
    try {
      if (typeof cartItem.sendAnalyticsEvent === 'function') {
        cartItem.sendAnalyticsEvent('Website', 'Floating Frame Upsell', 'Cart Page', data.diff / 100);
      }
    } catch (e) {}

    swap({
      lineKey: data.lineKey,
      fromVariantId: data.from,
      toVariantId: data.to,
      quantity: currentQuantity,
      sourceGenerationId: data.generationId
    }).then(function () {
      window.location.reload(true);
    }).catch(function (error) {
      cartItem.removeAttribute('data-nc-frame-busy');
      Array.prototype.forEach.call(disabledControls, function (item) {
        item.removeAttribute('disabled');
      });
      if (window.console && typeof window.console.error === 'function') window.console.error('Frame upgrade failed', error);
      if (typeof window.showUpcartErrorToast === 'function') window.showUpcartErrorToast();
      else if (typeof window.alert === 'function') window.alert('We could not complete the frame upgrade. Please refresh your cart and try again.');
    });
  }

  window.NCCartFrameSwap = { snapshot: snapshot, swap: swap };
  document.addEventListener('click', onCartPageUpgrade, true);
})();
